import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Edit, Printer, Save } from "lucide-react";
import {
  Item, Loan, LocationRecord, MOVE_REASONS, Movement,
  CONDITIONS, ITEM_CATEGORIES, SEASONS, ACQUISITION_TYPES,
  downloadAuthed, dueDescriptor, fileUrl, formatMoney,
  itemsApi, loansApi, locationsApi, taxonomyApi, timeAgo,
} from "@/lib/catalog-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhotoUploader } from "@/components/catalog/PhotoUploader";
import { Skeleton } from "@/components/catalog/Skeleton";
import { SearchSelect } from "@/components/catalog/OptionPicker";
import { ItemAttributeFields } from "@/components/catalog/ItemAttributeFields";
import { flat } from "@/lib/item-options";

export default function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [item, setItem] = useState<Item | null>(null);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [locations, setLocations] = useState<LocationRecord[]>([]);
  const [tab, setTab] = useState<"overview" | "history" | "loans" | "notes">("overview");
  const [editing, setEditing] = useState(params.get("new") === "1");
  const [draft, setDraft] = useState<Partial<Item>>({});
  const [saving, setSaving] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [loans, setLoans] = useState<Loan[] | null>(null);
  const [categoryOptions, setCategoryOptions] = useState<string[]>(ITEM_CATEGORIES as unknown as string[]);
  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [locationTypeOptions, setLocationTypeOptions] = useState<string[]>([]);

  const load = useCallback(async () => {
    if (!id) return;
    const [it, mv] = await Promise.all([itemsApi.get(id), itemsApi.movements(id)]);
    setItem(it); setMovements(mv); setDraft({});
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    locationsApi.list().then(setLocations).catch(() => {});
    taxonomyApi.categories().then((r) => setCategoryOptions(r.categories)).catch(() => {});
    taxonomyApi.brands().then((r) => setBrandOptions(r.brands)).catch(() => {});
    taxonomyApi.locationTypes().then((r) => setLocationTypeOptions(r.location_types)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!id || tab !== "loans") return;
    setLoans(null);
    loansApi.list({ item_id: id }).then(setLoans).catch(() => setLoans([]));
  }, [id, tab]);

  if (!item) {
    return (
      <main className="flex-1 px-6 md:px-10 py-10">
        <Skeleton className="h-8 w-1/3 mb-6" />
        <div className="grid lg:grid-cols-2 gap-10">
          <Skeleton className="aspect-[4/5]" />
          <div className="space-y-3"><Skeleton className="h-4 w-1/2" /><Skeleton className="h-4 w-3/4" /><Skeleton className="h-4 w-2/3" /></div>
        </div>
      </main>
    );
  }

  async function save() {
    if (!item) return;
    if ("name" in draft && !String(draft.name ?? "").trim()) {
      alert("Item name can't be empty.");
      return;
    }
    setSaving(true);
    try {
      const payload = "name" in draft ? { ...draft, name: String(draft.name).trim() } : draft;
      const updated = await itemsApi.update(item._id, payload);
      setItem(updated); setDraft({}); setEditing(false);
    } catch (e: any) {
      alert(e?.message || "Save failed");
    } finally { setSaving(false); }
  }
  async function archive() {
    if (!item) return;
    if (!window.confirm("Archive this item? It will be hidden from the catalog. You can restore it from the archived view.")) return;
    await itemsApi.archive(item._id);
    navigate("/app/items");
  }
  function patch<K extends keyof Item>(key: K, value: Item[K] | undefined) {
    setDraft((d) => ({ ...d, [key]: value }));
  }
  const v = <K extends keyof Item>(key: K): any => (key in draft ? (draft as any)[key] : (item as any)[key]);

  const locationName = locations.find((l) => l._id === item.current_location_id)?.name;

  const primary = item.photos.find((p) => p.is_primary) || item.photos[0];

  return (
    <main className="flex-1 px-6 md:px-10 py-10" data-testid="item-detail-page">
      <button onClick={() => navigate(-1)} className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-oxblood inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft className="w-3 h-3" /> Back to items
      </button>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-10">
        <div>
          <div className="aspect-[4/5] bg-bone overflow-hidden mb-4">
            {primary ? <img src={fileUrl(primary.url)} alt={item.name} className="w-full h-full object-contain" />
              : <div className="w-full h-full grid place-items-center text-xs uppercase tracking-[0.25em] text-muted-foreground">No photo</div>}
          </div>
          <PhotoUploader itemId={item._id} photos={item.photos} onUpdated={(updated) => setItem(updated)} />
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">{item.brand || "—"} · {item.category} · {item.internal_code}</p>
          {editing ? (
            <input
              data-testid="item-detail-name-input"
              value={v("name") ?? ""}
              onChange={(e) => patch("name", e.target.value)}
              placeholder="Item name"
              className="font-display text-3xl sm:text-4xl mt-2 leading-tight w-full bg-transparent border-b border-oxblood/40 focus:border-oxblood outline-none pb-1"
            />
          ) : (
            <h1 className="font-display text-3xl sm:text-4xl mt-2 leading-tight" data-testid="item-detail-name">{item.name}</h1>
          )}

          <div className="flex flex-wrap items-center gap-2 mt-4">
            {item.size && <span className="text-xs px-2 py-0.5 border border-border">{item.size}</span>}
            <span className="text-xs px-2 py-0.5 border border-oxblood/30 text-oxblood">{item.condition}</span>
            {item.season && <span className="text-xs px-2 py-0.5 border border-border">{item.season}</span>}
            {item.archived && <span className="text-xs px-2 py-0.5 bg-oxblood text-ivory">Archived</span>}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Button onClick={() => setMoveOpen(true)} data-testid="item-move-btn" className="bg-foreground hover:bg-charcoal text-ivory rounded-none h-9 px-4 text-xs uppercase tracking-[0.18em]">
              Move item
            </Button>
            <Button onClick={() => downloadAuthed(`/items/${item._id}/label`, `${item.internal_code}.png`).catch(() => {})}
              data-testid="item-label-btn"
              variant="outline"
              className="bg-background hover:bg-bone/60 border border-border text-foreground rounded-none h-9 px-4 text-xs uppercase tracking-[0.18em]">
              <Printer className="w-3.5 h-3.5 mr-1.5" /> Label
            </Button>
            <Button onClick={() => setEditing((v) => !v)} data-testid="item-edit-btn" className="bg-background hover:bg-bone/60 border border-border text-foreground rounded-none h-9 px-4 text-xs uppercase tracking-[0.18em]">
              {editing ? <><Save className="w-3.5 h-3.5 mr-1.5" />Stop editing</> : <><Edit className="w-3.5 h-3.5 mr-1.5" />Edit</>}
            </Button>
            {editing && (
              <Button onClick={save} disabled={!Object.keys(draft).length || saving}
                data-testid="item-save-btn"
                className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-9 px-4 text-xs uppercase tracking-[0.18em]">
                {saving ? "Saving…" : "Save changes"}
              </Button>
            )}
          </div>

          <div className="mt-8 border-b border-border flex gap-6 text-sm">
            {(["overview", "history", "loans", "notes"] as const).map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`item-tab-${t}`}
                className={`pb-2 -mb-px border-b-2 capitalize transition-colors ${
                  tab === t ? "border-oxblood text-oxblood" : "border-transparent text-muted-foreground hover:text-foreground"
                }`}>{t === "history" ? "Movement history" : t === "loans" ? "Loan history" : t}</button>
            ))}
          </div>

          {tab === "overview" && (
            <div className="mt-6 grid sm:grid-cols-2 gap-5" data-testid="item-overview">
              <RowPicker label="Brand" editing={editing} value={v("brand")} options={brandOptions} testid="item-detail-brand"
                onChange={(x) => patch("brand", x ?? "")} />
              <RowPicker label="Category" editing={editing} value={v("category")} options={categoryOptions} testid="item-detail-category"
                onChange={(x) => patch("category", (x || "Other") as any)} />
              <Row label="Sub-category" editing={editing} value={v("sub_category") || ""} onChange={(x) => patch("sub_category", x)} />
              <div className="sm:col-span-2">
                <ItemAttributeFields prefix="item-detail" editing={editing} category={v("category")}
                  value={{ ...item, ...draft }} onPatch={(p) => setDraft((d) => ({ ...d, ...p }))} />
              </div>
              <RowSelect label="Condition" editing={editing} value={v("condition")} options={CONDITIONS as any} onChange={(x) => patch("condition", x as any)} />
              <RowSelect label="Acquisition" editing={editing} value={v("acquisition_type")} options={ACQUISITION_TYPES as any} onChange={(x) => patch("acquisition_type", x as any)} />
              <RowSelect label="Season" editing={editing} value={v("season") || ""} options={["", ...SEASONS] as any} onChange={(x) => patch("season", (x || undefined) as any)} />
              <Row label="Retail value" editing={editing} value={String(v("retail_value") ?? "")}
                onChange={(x) => patch("retail_value", (x ? Number(x) : undefined) as any)}
                display={formatMoney(item.retail_value)} type="number" />
              <Row label="Insured value" editing={editing} value={String(v("insured_value") ?? "")}
                onChange={(x) => patch("insured_value", (x ? Number(x) : undefined) as any)}
                display={formatMoney(item.insured_value)} type="number" />
              <RowDatalist label="Location" editing={editing}
                value={(v("current_location_name") ?? locationName ?? "") as string}
                options={locations.map((l) => l.name)} listId="location-opts-detail"
                onChange={(x) => patch("current_location_name", x)}
                display={locationName || "—"} />
              <RowDatalist label="Location type" editing={editing}
                value={v("location_type") || ""}
                options={locationTypeOptions} listId="location-type-opts-detail"
                onChange={(x) => patch("location_type", x)} />
              <Row label="Tags" editing={editing}
                value={(v("tags") || []).join(", ")}
                onChange={(x) => patch("tags", x.split(",").map((t: string) => t.trim()).filter(Boolean) as any)}
                display={(item.tags || []).join(", ") || "—"} />
              <Row label="SKU" editing={editing} value={v("sku") || ""} onChange={(x) => patch("sku", x)}
                display={item.sku || "—"} />
              <Row label="Box" editing={editing} value={v("box") || ""} onChange={(x) => patch("box", x)}
                display={item.box || "—"} />
            </div>
          )}

          {tab === "history" && (
            <div className="mt-6 space-y-4" data-testid="item-history">
              {movements.length === 0 ? (
                <p className="text-sm text-muted-foreground">No movement history yet.</p>
              ) : movements.map((m) => (
                <div key={m._id} className="border-l-2 border-oxblood/40 pl-4 text-sm">
                  <p>
                    <span className="text-muted-foreground">From</span> {locationName_(locations, m.from_location_id)}
                    <ArrowRight className="inline w-3 h-3 mx-1 -mt-0.5" />
                    <span className="text-foreground font-medium">{locationName_(locations, m.to_location_id)}</span>
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {timeAgo(m.moved_at)}{m.reason ? ` · ${m.reason}` : ""}{m.notes ? ` · ${m.notes}` : ""}
                  </p>
                </div>
              ))}
            </div>
          )}

          {tab === "loans" && (
            <div className="mt-6 space-y-3" data-testid="item-loans">
              {loans === null ? (
                <p className="text-sm text-muted-foreground">Loading loan history…</p>
              ) : loans.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="item-loans-empty">
                  This piece has never been on loan.
                </p>
              ) : (
                <ul className="divide-y divide-border border-y border-border">
                  {loans.map((ln) => {
                    const due = dueDescriptor(ln.due_at);
                    const tone = ln.effective_status === "Overdue" ? "bad" :
                      ln.effective_status === "Returned" ? "ok" :
                      ln.effective_status === "Lost" ? "bad" : due.tone;
                    const pillCls =
                      tone === "bad" ? "bg-oxblood text-ivory" :
                      tone === "warn" ? "bg-oxblood/15 text-oxblood-deep" :
                      "bg-foreground/5 text-muted-foreground";
                    return (
                      <li key={ln._id} data-testid={`item-loan-${ln._id}`}
                        className="grid grid-cols-[1fr_auto] gap-3 py-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm truncate">{ln.borrower_name}</span>
                            <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{ln.borrower_type}</span>
                            <span className={`text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 ${pillCls}`}>
                              {ln.effective_status}
                            </span>
                          </div>
                          {ln.purpose && (
                            <p className="text-[12px] text-foreground/80 mt-0.5 truncate">{ln.purpose}</p>
                          )}
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            Out {new Date(ln.picked_up_at).toLocaleDateString()} · due {new Date(ln.due_at).toLocaleDateString()}
                            {ln.returned_at && ` · returned ${new Date(ln.returned_at).toLocaleDateString()}`}
                            {ln.return_condition && ` · ${ln.return_condition}`}
                          </p>
                          {ln.return_notes && (
                            <p className="text-[11px] italic text-muted-foreground mt-0.5">{`\u201C${ln.return_notes}\u201D`}</p>
                          )}
                        </div>
                        <Link to={`/app/loans?item_id=${item._id}`}
                          data-testid={`item-loan-link-${ln._id}`}
                          className="text-[11px] uppercase tracking-[0.18em] text-oxblood hover:underline underline-offset-4 self-start">
                          Open →
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {tab === "notes" && (
            <div className="mt-6 space-y-4" data-testid="item-notes">
              <textarea readOnly={!editing}
                value={v("notes") || ""}
                onChange={(e) => patch("notes", e.target.value)}
                rows={6} className="w-full border border-border bg-background p-3 text-sm" />
              <textarea readOnly={!editing}
                value={v("condition_notes") || ""}
                onChange={(e) => patch("condition_notes", e.target.value)}
                placeholder="Condition / care / repair notes…"
                rows={4} className="w-full border border-border bg-background p-3 text-sm" />
            </div>
          )}

          {!item.archived && (
            <div className="mt-12 pt-6 border-t border-border">
              <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Danger zone</p>
              <Button onClick={archive} data-testid="item-archive-btn"
                className="bg-background hover:bg-oxblood/5 border border-oxblood/40 text-oxblood rounded-none h-9 px-4 text-xs uppercase tracking-[0.18em]">
                Archive item
              </Button>
            </div>
          )}
        </div>
      </div>

      {moveOpen && <MoveDialog item={item} locations={locations} onClose={() => setMoveOpen(false)}
        onMoved={(updated) => { setItem(updated); setMoveOpen(false); load(); }} />}
    </main>
  );
}

function locationName_(locs: LocationRecord[], id?: string | null) {
  if (!id) return <span className="text-muted-foreground">—</span>;
  return locs.find((l) => l._id === id)?.name || "—";
}

function Row({ label, value, onChange, editing, display, type }: { label: string; value: string; onChange: (x: string) => void; editing: boolean; display?: any; type?: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">{label}</p>
      {editing ? (
        <Input value={value} onChange={(e: any) => onChange(e.target.value)} type={type || "text"} />
      ) : (
        <p className="text-sm">{display ?? (value || "—")}</p>
      )}
    </div>
  );
}

function RowSelect({ label, value, onChange, editing, options, display }: { label: string; value: string; onChange: (x: string) => void; editing: boolean; options: any[]; display?: any }) {
  const opts = options.map((o: any) => typeof o === "string" ? { v: o, l: o || "—" } : o);
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">{label}</p>
      {editing ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} className="h-10 w-full border border-border bg-background px-2 text-sm">
          {opts.map((o: any) => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      ) : (
        <p className="text-sm">{display ?? (opts.find((o: any) => o.v === value)?.l || "—")}</p>
      )}
    </div>
  );
}

function RowPicker({ label, value, onChange, editing, options, testid }: { label: string; value?: string | null; onChange: (x: string | null) => void; editing: boolean; options: string[]; testid: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">{label}</p>
      {editing ? (
        <SearchSelect testid={testid} value={value} allowCustom groups={flat(options)} onChange={onChange} />
      ) : (
        <p className="text-sm" data-testid={`${testid}-value`}>{value || "—"}</p>
      )}
    </div>
  );
}

function RowDatalist({ label, value, onChange, editing, options, listId, display }: { label: string; value: string; onChange: (x: string) => void; editing: boolean; options: string[]; listId: string; display?: any }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">{label}</p>
      {editing ? (
        <>
          <Input value={value} list={listId} onChange={(e: any) => onChange(e.target.value)} />
          <datalist id={listId}>
            {options.map((o) => <option key={o} value={o} />)}
          </datalist>
        </>
      ) : (
        <p className="text-sm">{display ?? (value || "—")}</p>
      )}
    </div>
  );
}

function MoveDialog({ item, locations, onClose, onMoved }: { item: Item; locations: LocationRecord[]; onClose: () => void; onMoved: (item: Item) => void }) {
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("Other");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!target) { setError("Pick a destination."); return; }
    setBusy(true); setError(null);
    try { onMoved(await itemsApi.move(item._id, { to_location_id: target, reason, notes: notes || undefined })); }
    catch (e: any) { setError(e?.message || "Move failed"); }
    finally { setBusy(false); }
  }
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-charcoal/40 backdrop-blur-sm px-6" data-testid="move-dialog">
      <div className="bg-background border border-border max-w-md w-full p-6">
        <p className="text-[11px] uppercase tracking-[0.22em] text-oxblood mb-2">— Move item</p>
        <h3 className="font-display text-2xl">{item.name}</h3>
        <div className="mt-5 space-y-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">Destination</p>
            <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="move-target"
              className="h-10 w-full border border-border bg-background px-2 text-sm">
              <option value="">Choose location…</option>
              {locations.filter((l) => !l.archived && l._id !== item.current_location_id).map((l) =>
                <option key={l._id} value={l._id}>{l.name}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">Reason</p>
            <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="move-reason"
              className="h-10 w-full border border-border bg-background px-2 text-sm">
              {MOVE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">Notes</p>
            <Input value={notes} onChange={(e: any) => setNotes(e.target.value)} data-testid="move-notes" />
          </div>
          {error && <p className="text-sm text-oxblood">{error}</p>}
        </div>
        <div className="mt-6 flex gap-3">
          <Button onClick={submit} disabled={busy} data-testid="move-submit"
            className="flex-1 bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-11">
            {busy ? "Moving…" : "Confirm move"}
          </Button>
          <Button onClick={onClose} className="bg-background border border-border hover:bg-bone/60 rounded-none h-11">Cancel</Button>
        </div>
      </div>
    </div>
  );
}
